import React from 'react';
import { 
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormHelperText,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    SvgIconTypeMap,
    Typography,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { FormikErrors } from 'formik';

import { styles } from './styles';

interface Props {
    label: string,
    name: string,
    placeholder: string,
    isFromTag?: boolean,
    values: string[],
    error: boolean,
    errorText: string,
    Icon?: OverridableComponent<SvgIconTypeMap> & { muiName: string },
    emptyText: string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<FormikErrors<any>> | Promise<void>;
    options: { value: string, label: string, color?: string, bg?: string }[],
    enableAdd?: boolean,
    onEnableAdd?(): void,
}

const MultiSelect: React.FC<Props> = ({
    error,
    errorText,
    values,
    name,
    isFromTag = false,
    label,
    Icon,
    placeholder,
    emptyText,
    options,
    enableAdd = false,
    onEnableAdd,
    setFieldValue,
}) => {
    return (
        <>
            <InputLabel
                sx={{ ...styles.inputLabel, mt: 3 }}
            >
                {Icon && <Icon style={styles?.labelIcon} />}{'\u00A0'}{label}
            </InputLabel>
            <FormControl fullWidth size="small">
                <Select
                    multiple
                    displayEmpty
                    value={values}
                    error={error}
                    renderValue={(selected: string[]) => {
                        const selectedNames: string[] = [];

                        selected?.forEach(s => {
                            const name = options?.find(op => op?.value === s)?.label!;

                            if (name) {
                                selectedNames?.push(name);
                            }
                        });

                        if (selectedNames?.length === 0) {
                            return <span>{placeholder}</span>;
                        }   

                        if (isFromTag) {
                            return selectedNames?.map(s => (
                                <Chip 
                                    label={s?.toLowerCase()} 
                                    size="small" 
                                    sx={{ 
                                        mr: 0.5,
                                        background: options?.find(op => op?.label === s)?.bg,
                                        color: options?.find(op => op?.label === s)?.color
                                    }} 
                                    variant="filled" 
                                />
                            )) 
                        }

                        return selectedNames?.join(', ');
                    }}
                    onChange={({ target: { value }}) => {
                        setFieldValue(name, typeof value === 'string' ? value.split(',') : value);
                    }}
                >
                    {options?.length > 0 ? options?.map(option => (
                        <MenuItem value={option?.value} key={option?.value}>
                            <Checkbox checked={values?.indexOf(option?.value!) > -1} />
                            {isFromTag ? 
                                <>
                                    <Chip 
                                        label={option?.label?.toLowerCase()} 
                                        size="small" 
                                        sx={{ 
                                            mr: 0.5,
                                            background: option?.bg,
                                            color: option?.color
                                        }} 
                                        variant="filled" 
                                    />
                                </> :
                                <ListItemText primary={option?.label} />
                            }
                            
                        </MenuItem>
                    )) : <Stack alignContent={'center'}>
                            <Typography sx={{ p: 1 }} textAlign={'center'} variant='body2'>{emptyText}</Typography>
                            {enableAdd && <Button onClick={onEnableAdd} fullWidth={false} size='small' variant='contained'>Adicionar</Button>}
                        </Stack>
                    }
                </Select>
                {error &&
                    <FormHelperText error style={{ color: '#ff0000' }}>
                        {errorText}
                    </FormHelperText>
                }
            </FormControl>
        </>
    )
}

export default MultiSelect;